import React, { useMemo, useState } from "react";
import {
  CForm,
  CFormInput,
  CModal,
  CModalTitle,
  CModalHeader,
  CModalBody,
  CFormLabel,
  CModalFooter,
  CFormSelect,
  CFormSwitch,
  CButton,
  CTable,
} from "@coreui/react";
import { apiClient } from "services/Api";
import { triggerAlert } from "components/globals/AlertNotification";

const RESPONSIBLE_ENTITIES = [
  { value: "IAPMEI", label: "IAPMEI" },
  { value: "ANI", label: "ANI" },
  { value: "OUTRO", label: "Outro" },
];

const INVESTMENT_METHODS = [
  { value: "CUSTOS_REAIS", label: "Custos Reais" },
  { value: "CUSTOS_SIMPLIFICADOS", label: "Custos Simplificados" },
];

const PROJECT_STATE_OPTIONS = [
  { value: "APPROVED", label: "Aprovado" },
  { value: "THEORETICAL", label: "Teórico" },
];

const ANI_TRLS = [
  "Investigação Industrial",
  "Desenvolvimento Experimental",
  "Divulgação e Promoção",
  "Gestão de Projeto",
];

const IAPMEI_TRLS = ["TRL 3-4", "TRL 5-9"];

const NewProject = ({ clientId, onClose, setToast }) => {
  const [projectName, setProjectName] = useState("");
  const [program, setProgram] = useState("");
  const [code, setCode] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [state, setState] = useState("APPROVED");
  const [responsibleEntity, setResponsibleEntity] = useState("ANI");
  const [investMethodology, setInvestMethodology] = useState(
    "CUSTOS_SIMPLIFICADOS",
  );
  const [approvedBudget, setApprovedBudget] = useState(0);
  const [hourCost, setHourCost] = useState("");
  const [incentiveRates, setIncentiveRates] = useState({});
  const [noIncentive, setNoIncentive] = useState(false);
  const [errors, setErrors] = useState({});
  const isTheoretical = state === "THEORETICAL";

  const currentTrls = useMemo(() => {
    if (responsibleEntity === "ANI") return ANI_TRLS;
    if (responsibleEntity === "IAPMEI") return IAPMEI_TRLS;
    return [];
  }, [responsibleEntity]);

  const renderFieldError = (field) => {
    const message = errors?.[field];
    if (!message) return null;
    const text = Array.isArray(message) ? message.join(" ") : message;
    return <small className="text-danger">{text}</small>;
  };

  const renderNonFieldErrors = () => {
    const message = errors?.non_field_errors;
    if (!message) return null;
    const text = Array.isArray(message) ? message.join(" ") : message;
    return <div className="alert alert-danger py-2 mb-3">{text}</div>;
  };

  const handleRateChange = (trl, value) => {
    setIncentiveRates((prev) => ({
      ...prev,
      [trl]: value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!projectName.trim()) newErrors.projectName = "O nome do projeto é obrigatório.";
    if (!startDate) newErrors.startDate = "A data de início é obrigatória.";
    if (!endDate) newErrors.endDate = "A data de conclusão é obrigatória.";
    if (!noIncentive) {
      if (!state) newErrors.state = "O estado do projeto é obrigatório.";
      if (!responsibleEntity) {
        newErrors.responsibleEntity = "A entidade responsável é obrigatória.";
      }
      if (!investMethodology) {
        newErrors.investMethodology =
          "A tipologia do investimento é obrigatória.";
      }
      if (investMethodology === "CUSTOS_SIMPLIFICADOS" && hourCost === "") {
        newErrors.hourCost =
          "O custo ETI é obrigatório para Custos Simplificados.";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateProject = (e) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) return;

    const incentiveRatesPayload =
      noIncentive || isTheoretical
        ? []
        : currentTrls
        .filter(
          (trl) => incentiveRates[trl] !== undefined && incentiveRates[trl] !== "",
        )
        .map((trl) => ({
          trl,
          rate: Number(incentiveRates[trl]),
        }));

    const payload = {
      project_name: projectName,
      program: noIncentive ? "" : program,
      code: isTheoretical || noIncentive ? "" : code,
      start_date: startDate,
      end_date: endDate,
      state: noIncentive ? "APPROVED" : state,
      responsible_entity: noIncentive ? "OUTRO" : responsibleEntity,
      investment_methodology: noIncentive ? "CUSTOS_REAIS" : investMethodology,
      approved_budget: isTheoretical || noIncentive ? 0 : Number(approvedBudget || 0),
      hour_cost:
        !noIncentive && investMethodology === "CUSTOS_SIMPLIFICADOS"
          ? Number(hourCost || 0)
          : null,
      no_incentive: noIncentive,
      incentive_rates: incentiveRatesPayload,
    };

    apiClient
      .post(`/projects/management/${clientId}/project/`, payload)
      .then(() => {
        triggerAlert("Projeto criado com sucesso!", "success");
        window.location.reload();
      })
      .catch((error) => {
        const resp = error.response;
        if (resp && resp.status === 400 && resp.data) {
          setErrors(resp.data);
          return;
        }

        setToast({
          title: "Novo Projeto",
          message: "Erro a criar o projeto",
          type: "error",
        });
      });
  };

  return (
    <CModal onClose={onClose} size="lg" backdrop="static" scrollable visible>
      <CModalHeader>
        <CModalTitle>Criar Novo Projeto</CModalTitle>
      </CModalHeader>
      <CModalBody>
        <CForm className="row g-3">
          {renderNonFieldErrors()}

          <div className="col-md-8">
            <CFormLabel htmlFor="projectName">
              Nome do Projeto <span className="text-danger">*</span>
            </CFormLabel>
            <CFormInput
              id="projectName"
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              placeholder="Introduz o nome do projeto"
            />
            {errors.projectName && (
              <small className="text-danger">{errors.projectName}</small>
            )}
            {renderFieldError("project_name")}
          </div>

          <div className="col-md-4 d-flex align-items-center justify-content-end">
            <CFormSwitch
              id="noIncentive"
              label="Sem Incentivo"
              checked={noIncentive}
              onChange={(e) => setNoIncentive(e.target.checked)}
            />
          </div>

          <div className="col-md-6">
            <CFormLabel htmlFor="startDate">
              Data de Início <span className="text-danger">*</span>
            </CFormLabel>
            <CFormInput
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            {errors.startDate && (
              <small className="text-danger">{errors.startDate}</small>
            )}
            {renderFieldError("start_date")}
          </div>

          <div className="col-md-6">
            <CFormLabel htmlFor="endDate">
              Data de Conclusão <span className="text-danger">*</span>
            </CFormLabel>
            <CFormInput
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            {errors.endDate && (
              <small className="text-danger">{errors.endDate}</small>
            )}
            {renderFieldError("end_date")}
          </div>

          {!noIncentive && (
            <div className="col-md-6">
              <CFormLabel htmlFor="projectState">
                Estado do Projeto <span className="text-danger">*</span>
              </CFormLabel>
              <CFormSelect
                id="projectState"
                value={state}
                options={PROJECT_STATE_OPTIONS}
                onChange={(e) => setState(e.target.value)}
              />
              {errors.state && <small className="text-danger">{errors.state}</small>}
              {renderFieldError("state")}
            </div>
          )}

          {!noIncentive && (
            <div className="col-md-6">
              <CFormLabel htmlFor="responsibleEntity">
                Entidade Responsável <span className="text-danger">*</span>
              </CFormLabel>
              <CFormSelect
                id="responsibleEntity"
                value={responsibleEntity}
                options={RESPONSIBLE_ENTITIES}
                onChange={(e) => setResponsibleEntity(e.target.value)}
              />
              {errors.responsibleEntity && (
                <small className="text-danger">{errors.responsibleEntity}</small>
              )}
              {renderFieldError("responsible_entity")}
            </div>
          )}

          {!noIncentive && (
            <div className="col-md-6">
              <CFormLabel htmlFor="investMethodology">
                Tipologia do Investimento <span className="text-danger">*</span>
              </CFormLabel>
              <CFormSelect
                id="investMethodology"
                value={investMethodology}
                options={INVESTMENT_METHODS}
                onChange={(e) => setInvestMethodology(e.target.value)}
              />
              {errors.investMethodology && (
                <small className="text-danger">{errors.investMethodology}</small>
              )}
              {renderFieldError("investment_methodology")}
            </div>
          )}

          {!noIncentive && (
            <div className="col-md-6">
              <CFormLabel htmlFor="program">Programa de Financiamento</CFormLabel>
              <CFormInput
                id="program"
                type="text"
                value={program}
                onChange={(e) => setProgram(e.target.value)}
                placeholder="Introduz o programa de financiamento"
              />
            </div>
          )}

          {!isTheoretical && !noIncentive && (
            <div className="col-md-6">
              <CFormLabel htmlFor="code">Código do Projeto</CFormLabel>
              <CFormInput
                id="code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Introduz o código do projeto"
              />
            </div>
          )}

          {!isTheoretical && !noIncentive && (
            <div className="col-md-6">
              <CFormLabel htmlFor="approvedBudget">Incentivo Aprovado</CFormLabel>
              <CFormInput
                id="approvedBudget"
                type="number"
                value={approvedBudget}
                onChange={(e) => setApprovedBudget(e.target.value)}
                placeholder="Introduz o montante aprovado"
              />
            </div>
          )}

          {!noIncentive && investMethodology === "CUSTOS_SIMPLIFICADOS" && (
            <div className="col-md-6">
              <CFormLabel htmlFor="etiCost">
                Custo ETI <span className="text-danger">*</span>
              </CFormLabel>
              <CFormInput
                id="etiCost"
                type="number"
                value={hourCost}
                onChange={(e) => setHourCost(e.target.value)}
                placeholder="Introduz o custo ETI"
              />
              {errors.hourCost && (
                <small className="text-danger">{errors.hourCost}</small>
              )}
              {renderFieldError("hour_cost")}
            </div>
          )}

          {!noIncentive && !isTheoretical && currentTrls.length > 0 && (
            <div className="col-12">
              <CFormLabel>Taxas de Incentivo por TRL</CFormLabel>
              <CTable small bordered className="mt-2">
                <thead>
                  <tr>
                    <th>TRL</th>
                    <th>Taxa de Incentivo (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {currentTrls.map((trl) => (
                    <tr key={trl}>
                      <td>{trl}</td>
                      <td>
                        <CFormInput
                          type="number"
                          min={0}
                          max={100}
                          value={incentiveRates[trl] ?? ""}
                          onChange={(e) => handleRateChange(trl, e.target.value)}
                          placeholder="0"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </CTable>
            </div>
          )}
        </CForm>
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" variant="outline" onClick={onClose}>
          Fechar
        </CButton>
        <CButton color="primary" onClick={handleCreateProject}>
          Criar Projeto
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default NewProject;
